import React, { useState, useEffect } from 'react';
import axiosInstance from '../utils/axiosInstance';
import SquareModal from './SquareModal';
import ShoppingCart from './ShoppingCart';
import '../assets/css/grid.css';

const Grid = () => {
    const [squares, setSquares] = useState([]);
    const [remainingSquares, setRemainingSquares] = useState(100);
    const [players, setPlayers] = useState([]);
    const [isModalOpen, setModalOpen] = useState(false);
    const [selectedSquare, setSelectedSquare] = useState(null);
    const [selectedPlayerName, setSelectedPlayerName] = useState(''); // Track selected player
    const [cart, setCart] = useState([]);
    const [boardFrozen, setBoardFrozen] = useState(false);
    const [topLabels, setTopLabels] = useState([]);
    const [sideLabels, setSideLabels] = useState([]);
    const [homeTeam, setHomeTeam] = useState({ name: 'Home Team', color: '#CCCCCC' });
    const [awayTeam, setAwayTeam] = useState({ name: 'Away Team', color: '#CCCCCC' });
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState('');
    const [price, setPrice] = useState(0); // Dynamically fetched price
    const labels = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J'];

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            setErrorMessage('');
            try {
                const [squareResponse, playerResponse, boardResponse, labelResponse, teamResponse, priceResponse] = await Promise.all([
                    axiosInstance.get('/squares'),
                    axiosInstance.get('/players'),
                    axiosInstance.get('/board/status'),
                    axiosInstance.get('/board/grid-labels'),
                    axiosInstance.get('/board/add-teams'),
                    axiosInstance.get('/board/price'), // Fetch price
                ]);

                const squaresData = (squareResponse.data || [])
                    .filter((square) => square.id)
                    .map((square) => ({
                        ...square,
                        normalizedId: square.id.trim().toUpperCase(),
                    }))
                    .sort((a, b) => a.normalizedId.localeCompare(b.normalizedId));

                const playersData = (playerResponse.data || []).sort((a, b) => 
                    a.name.localeCompare(b.name)
                );
                const boardStatus = boardResponse.data || { isFrozen: false };
                const labelData = labelResponse.data || { published: false };
                const teamData = teamResponse.data || { homeTeam: {}, awayTeam: {} };

                const processedSquares = squaresData.map((square) => {
                    if (square.isPurchased) {
                        const player = playersData.find((player) => player.name === square.playerName);
                        return {
                            ...square,
                            playerColor: player ? player.color : '#ccc',
                        };
                    }
                    return square;
                });

                setSquares(processedSquares);
                setPlayers(playersData);
                setRemainingSquares(processedSquares.filter((square) => !square.isPurchased).length);
                setBoardFrozen(boardStatus.isFrozen);
                setHomeTeam(teamData.homeTeam || { name: 'Home Team', color: '#ff0000' });
                setAwayTeam(teamData.awayTeam || { name: 'Away Team', color: '#0000ff' });

                if (labelData.published) {
                    setTopLabels(labelData.topLabels);
                    setSideLabels(labelData.sideLabels);
                } else {
                    setTopLabels([]);
                    setSideLabels([]);
                }

                setPrice(priceResponse.data.price || 0); // Set fetched price
            } catch (error) {
                console.error('Error fetching grid data:', error);
                setErrorMessage('Failed to load grid data. Please try again later.');
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, []);

    const handleSquareClick = (square) => {
        if (square.isPurchased) {
            return;
        }
        console.log('Selected Square:', square); // Log the clicked square
        setSelectedSquare(square);
        setSelectedPlayerName(''); // Reset player name for each new selection
        setModalOpen(true);
    };
    
    const handleAddToCart = (square) => {
        console.log('Adding square to cart:', square);
        const item = {
            squareId: square.squareId,
            playerName: square.playerName, // Player selected in modal
            userName: square.userName, // User name from modal
            email: square.email, // User email from modal
            price: price, // Current price of square
        };
    
        setCart((prevCart) => [...prevCart, item]);
        setSquares((prevSquares) =>
            prevSquares.map((sq) => (sq.id === square.squareId ? { ...sq, inCart: true } : sq))
        );
        setModalOpen(false);
    };
    
    const handleRemoveFromCart = (itemToRemove) => {
        setCart((prevCart) => prevCart.filter((item) => item.squareId !== itemToRemove.squareId));

        setSquares((prevSquares) =>
            prevSquares.map((square) => {
                if (square.id === itemToRemove.squareId) {
                    return { ...square, inCart: false };
                }
                return square;
            })
        );
    };

    const handleCheckout = async (cart) => {
        try {
            const response = await fetch('/checkout', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    cart: cart.map((item) => item.squareId),
                }),
            });

            return response;
        } catch (error) {
            console.error('Checkout error:', error);
            return { ok: false, message: 'Network error occurred.' };
        }
    };

    if (isLoading) {
        return <div className="loading-message">Loading grid data...</div>;
    }

    return (
        <div className="grid-container">
            {errorMessage && <div className="error-message">{errorMessage}</div>}
            <ShoppingCart cart={cart} onCheckout={handleCheckout} onRemoveFromCart={handleRemoveFromCart} />
            <div className="message-container">
                <strong>{remainingSquares}</strong> squares left to sell!
            </div>
            <table className="grid-table">
                <thead>
                    <tr>
                        <th></th>
                        <th colSpan="11" className="home" style={{ color: homeTeam.color }}>
                            {homeTeam.name}
                        </th>
                    </tr>
                    <tr>
                        <th></th>
                        <th colSpan="11">
                            <table className="player-key">
                                <tbody>
                                    <tr>
                                        {players.map((player, index) => (
                                            <td key={player.name} className="player-key-item">
                                                <span
                                                    className="player-key-circle"
                                                    style={{
                                                        backgroundColor: player.color,
                                                        display: 'inline-block',
                                                        width: '12px',
                                                        height: '12px',
                                                        borderRadius: '50%',
                                                        marginRight: '5px',
                                                    }}
                                                ></span>
                                                {player.name}
                                                {index < players.length - 1 && ','}
                                            </td>
                                        ))}
                                    </tr>
                                </tbody>
                            </table>
                        </th>
                    </tr>
                    <tr>
                        <th></th>
                        <th></th>
                        {topLabels.length > 0
                            ? topLabels.map((label, index) => (
                                  <th key={`top-${index}`} className="grid-label">
                                      {label}
                                  </th>
                              ))
                            : labels.map((label, index) => (
                                  <th key={`label-${index}`} className="grid-label">
                                      {label}
                                  </th>
                              ))}
                    </tr>
                </thead>
                <tbody>
                    {(sideLabels.length > 0 ? sideLabels : labels).map((rowLabel, rowIndex) => {
                        const rowSquares = squares.filter((square) =>
                            square.normalizedId?.startsWith(labels[rowIndex])
                        );

                        return (
                            <tr key={`row-${rowIndex}`}>
                                {rowIndex === 0 && (
                                    <th className="away" rowSpan="10" style={{ color: awayTeam.color }}>
                                        {awayTeam.name}
                                    </th>
                                )}
                                <th className="grid-label">{rowLabel}</th>
                                {rowSquares.map((square, colIndex) => (
                                    <td
                                        key={`cell-${rowIndex}-${colIndex}`}
                                        className={`grid-square ${
                                            square.isPurchased
                                                ? 'purchased'
                                                : square.inCart
                                                ? 'in-cart'
                                                : 'available cell-hover'
                                        }`}
                                        style={
                                            square.isPurchased
                                                ? { backgroundColor: square.playerColor }
                                                : {}
                                        }
                                        onClick={() => {
                                            if (!square.isPurchased && !square.inCart) {
                                                handleSquareClick(square);
                                            }
                                        }}
                                    >
                                        <div className="square-content">
                                            {square.isPurchased ? (
                                                <div className="purchaser-name">
                                                    {square.purchaserName}
                                                </div>
                                            ) : square.inCart ? (
                                                <div className="cart-message">In Cart</div>
                                            ) : (
                                                <div className="purchase-info">
                                                    <span>${price}</span>
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                ))}
                            </tr>
                        );
                    })}
                </tbody>
            </table>

            <SquareModal
                isOpen={isModalOpen}
                onClose={() => setModalOpen(false)}
                onAddToCart={handleAddToCart}
                selectedSquare={selectedSquare}
                selectedPlayerName={selectedPlayerName}
                onPlayerChange={setSelectedPlayerName}
                boardFrozen={boardFrozen}
                price={price}
            />
        </div>
    );
};

export default Grid;